// Operations › Profit, Blueprint only (pages/ux-full screens/ads.js: #/home/profit, #/home/profit/jobs), on
// DashboardPage (BL-189), each section from its own action's answer: what yesterday's jobs made after
// their materials, purchases, receipts and paid labour, against the target margin (reports.jobCosting),
// beside the month against the goal (gp.pace, GoalPace) and the huddle's last days (gp.huddle); and a day
// job by job and by technician, from each job's own record (jobs.get). What each table and row says is
// ui-web's rows/profit-rows.ts. Whoever holds finance.margins.view (profit-model.ts).
import { useEffect, useState } from 'react';
import { dateText } from '@flowology/kernel';
import {
  CostedJob,
  GoalPaceProps,
  HuddleDaysProps,
  JobCosting,
  plural,
  type OpenTarget,
} from '@flowology/ui-web';
import {
  Button,
  DataTable,
  GoalPace,
  KeyValue,
  Label,
  Link,
  RowList,
  Segmented,
  huddleDayRows,
  huddleTargets,
  jobMarginColumns,
  jobMarginLines,
  notFinalRows,
  profitFacts,
  profitNote,
  shareRow,
  techColumns,
  techLines,
  unloggedRows,
} from '@flowology/ui-web/fl';
import { DashboardPage } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { addDays, todayIn } from '../books/model.ts';
import { useQuery } from '../books/ui.tsx';
import { jobHref } from '../job/job-page.ts';
import { answered, firstOf } from './dash-sections.ts';
import { officeHost } from './host.ts';
import './reports-page.css';

const tzOf = (nav: NavProps) => nav.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
const dayWords = (date: string, tz: string) => dateText(date, tz, { weekday: 'long', year: false });

/** A day's gross profit: its ledger and total, its share against the target, and what to check. */
function ProfitDay({ p, open }: { p: JobCosting; open: (jobId: string) => void }) {
  const share = shareRow(p);
  const waiting = notFinalRows(p, open);
  const unlogged = unloggedRows(p, open);
  const note = profitNote(p);
  return (
    <>
      <KeyValue layout="rows" items={profitFacts(p)} label="Gross profit" />
      {share && <RowList rows={[share]} label="Gross profit, as a share of revenue" />}
      <p className="bp-rep-sub">
        Labour is each person’s pay for their paid hours on the jobs, and their commissions, with{' '}
        {p.labourBurdenPct} % on top for the employer’s costs.
      </p>
      {waiting.length > 0 && (
        <>
          <p className="bp-rep-sub">
            <Label kind="wait">
              {waiting.length === 1
                ? '1 number isn’t final yet'
                : `${waiting.length} numbers aren’t final yet`}
            </Label>
          </p>
          <RowList rows={waiting} first={5} label="Not final yet" />
        </>
      )}
      {unlogged.length > 0 && (
        <>
          <p className="bp-rep-sub">
            <Label kind="wait">
              {unlogged.length === 1
                ? '1 job has no materials logged'
                : `${unlogged.length} jobs have no materials logged`}
            </Label>
          </p>
          <RowList rows={unlogged} first={5} label="No materials logged" />
        </>
      )}
      {note && <p className="bp-rep-sub">{note}</p>}
    </>
  );
}

// ── What did yesterday's work make? ──

export function ProfitScreen({ nav, onOpen }: { nav: NavProps; onOpen: (t: OpenTarget) => void }) {
  const tz = tzOf(nav);
  const yesterday = addDays(todayIn(tz), -1);
  const c = useQuery<unknown>('reports.jobCosting', { from: yesterday, to: yesterday });
  const pace = useQuery<unknown>('gp.pace', {});
  const h = useQuery<unknown>('gp.huddle', {});
  const host = officeHost(onOpen);
  const crumb = usePageCrumb(nav);
  const label = `Yesterday, ${dayWords(yesterday, tz)}`;
  const open = (id: string) => onOpen({ kind: 'job', id });
  return (
    <div className="app">
      <main>
        <PageBanners nav={nav} />
        <DashboardPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: 'Gross profit',
            lede: 'What yesterday’s jobs made after their materials, purchases and paid labour, and where the month stands.',
            actions: (
              <>
                <Button onPress={() => nav.onView('profitjobs')}>By job and technician</Button>
                <Button onPress={() => nav.onView('huddle')}>The month so far</Button>
              </>
            ),
          }}
          main={[
            answered(
              c,
              { title: 'Yesterday', what: 'yesterday’s jobs' },
              (d) => JobCosting.safeParse(d),
              (p) =>
                p.rows.length
                  ? {
                      children: (
                        <>
                          <p className="bp-rep-sub">
                            {label}, {plural(p.rows.length, 'job')} with revenue
                          </p>
                          <ProfitDay p={p} open={open} />
                        </>
                      ),
                    }
                  : { status: 'empty', emptyText: `No job made revenue ${label.toLowerCase()}.` },
            ),
          ]}
          side={[
            answered(
              pace,
              { title: 'The month against the goal', what: 'the month' },
              (d) => GoalPaceProps.safeParse(d),
              (p) => ({ children: <GoalPace data={p} host={host} /> }),
            ),
            answered(
              h,
              { title: 'The morning huddle', what: 'the huddle' },
              (d) => HuddleDaysProps.safeParse(d),
              (p) => {
                const days = huddleDayRows(p);
                return {
                  children: (
                    <>
                      <KeyValue
                        items={huddleTargets(p)}
                        layout="rows"
                        label="The targets the office plans by"
                      />
                      <RowList
                        rows={days.rows}
                        label="The last working days"
                        empty="No working day has passed this month yet."
                      />
                      {days.more && (
                        <p className="bp-rep-more">
                          <Link onPress={() => onOpen({ kind: 'numbers' })}>Every day of the month</Link>
                        </p>
                      )}
                    </>
                  ),
                };
              },
            ),
          ]}
        />
      </main>
    </div>
  );
}

// ── What did each job and technician make? ──

/** Each costed job's own record (jobs.get), for its customer and who was on it. */
function useJobs(ids: readonly string[]): { jobs: CostedJob[] | null; error: string | null } {
  const [jobs, setJobs] = useState<CostedJob[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const key = ids.join(',');
  useEffect(() => {
    let live = true;
    setJobs(null);
    void Promise.all(ids.map((id) => client.call<unknown>('jobs.get', { id })))
      .then((list) => live && setJobs(list.map((j) => CostedJob.parse(j))))
      .catch(() => live && setError('Couldn’t load who was on these jobs.'));
    return () => {
      live = false;
    };
    // the ids are compared by value (key), so a new list of the same jobs doesn't load them again
  }, [key]);
  return { jobs, error };
}

export function ProfitJobsScreen({ nav, onOpen }: { nav: NavProps; onOpen: (t: OpenTarget) => void }) {
  const tz = tzOf(nav);
  const yesterday = addDays(todayIn(tz), -1);
  const [day, setDay] = useState(yesterday);
  const [allJobs, setAllJobs] = useState(false);
  const [allTechs, setAllTechs] = useState(false);
  const c = useQuery<{ rows?: { jobId: string }[] }>('reports.jobCosting', { from: day, to: day });
  // only the shown day's answer, never the day before's under its heading (QA-682): while the next day
  // loads, or once it's refused, the page waits or says why
  const costing = c.current ? c.data : null;
  const { jobs, error } = useJobs(costing?.rows?.map((r) => r.jobId) ?? []);
  const crumb = usePageCrumb(nav);
  const words = dayWords(day, tz);
  const both = { ...c, data: costing && jobs ? costing : null, error: c.error ?? error };
  const step = (d: string) => {
    setDay(d);
    setAllJobs(false);
    setAllTechs(false);
  };
  const short = (d: string) => dateText(d, tz, { weekday: 'short', year: false });
  return (
    <div className="app">
      <main>
        <PageBanners nav={nav} />
        <DashboardPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: 'By job and technician',
            lede: 'Each job’s gross profit on a day, and each technician’s for every hour they logged.',
            actions: (
              <Segmented
                label="Day"
                selectionMode="none"
                items={[
                  { id: 'back', label: `‹ ${short(addDays(day, -1))}` },
                  { id: 'yesterday', label: 'Yesterday', isDisabled: day === yesterday },
                  { id: 'next', label: `${short(addDays(day, 1))} ›`, isDisabled: day >= yesterday },
                ]}
                onAction={(id) =>
                  step(id === 'back' ? addDays(day, -1) : id === 'next' ? addDays(day, 1) : yesterday)
                }
              />
            ),
          }}
          main={[
            answered(
              both,
              { title: `Jobs, ${words}`, id: 'jobs', what: 'the jobs' },
              (d) => JobCosting.safeParse(d),
              (p) => {
                if (!p.rows.length)
                  return { status: 'empty', emptyText: `No job made revenue ${words.toLowerCase()}.` };
                const lines = jobMarginLines(p, jobs ?? []);
                const shown = firstOf(lines.rows, 5, allJobs, () => setAllJobs(true));
                return {
                  count: p.rows.length,
                  children: (
                    <>
                      {p.targetMarginPct !== null && (
                        <p className="bp-rep-sub">
                          Jobs under the {p.targetMarginPct} % target margin are marked.
                        </p>
                      )}
                      <DataTable
                        columns={jobMarginColumns(p.targetMarginPct, p.rows.length)}
                        rows={shown.rows}
                        foot={lines.total}
                        hrefOf={(r) => jobHref(r.id)}
                        onOpen={(r) => onOpen({ kind: 'job', id: r.id })}
                        more={shown.more}
                        empty={`No job made revenue ${words.toLowerCase()}.`}
                      />
                    </>
                  ),
                };
              },
            ),
          ]}
          side={[
            answered(
              both,
              { title: 'By technician', what: 'who was on these jobs' },
              (d) => JobCosting.safeParse(d),
              (p) => {
                const lines = techLines(p.rows, jobs ?? []);
                if (!lines.length) return { status: 'empty', emptyText: 'Nobody is on these jobs.' };
                const target = p.targetMarginPct;
                const shown = firstOf(lines, 5, allTechs, () => setAllTechs(true));
                return {
                  children: (
                    <>
                      <p className="bp-rep-sub">
                        Each job shared by the paid time each technician has on it, the hours its labour
                        counts
                        {target !== null ? `; those under the ${target} % target margin are marked` : ''}.
                      </p>
                      <DataTable
                        columns={techColumns(lines, target)}
                        rows={shown.rows}
                        more={shown.more}
                        empty="Nobody is on these jobs."
                      />
                    </>
                  ),
                };
              },
            ),
          ]}
        />
      </main>
    </div>
  );
}
